import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { themes } from '../../themes/colors';
import { getBlogStyles } from '../../themes/styles/blog';
import { getCommonStyles } from '../../themes/styles/common';
import Navigation from '../CookAI/Navigaton/Navigation';
import Footer from '../CookAI/Footer/Footer';
import BlockRenderer from './BlockRenderer';
import { useLanguage } from '../../i18n/LanguageContext';
import { getBlog, sendScrollBeacon } from '../../services/blogApi';
import { BlogPost } from '../../types/blog';

const theme = themes['blue-tech'];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getOrCreateSessionId(): string {
  const key = 'cookai-blog-session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(key, id);
  }
  return id;
}

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const s = getBlogStyles(theme);
  const commonStyles = getCommonStyles(theme);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const firedDepths = useRef<Set<number>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 768) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    firedDepths.current.clear();
    window.scrollTo({ top: 0, behavior: 'auto' });

    getBlog(slug, language)
      .then((res) => {
        if (!res) {
          setNotFound(true);
        } else {
          setPost(res.data ?? res);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, language]);

  // Scroll depth tracking — fires beacons at 25 / 50 / 75 / 100 %
  useEffect(() => {
    if (!post || !slug) return;
    const sessionId = getOrCreateSessionId();
    const checkpoints = [25, 50, 75, 100];

    const handleScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const elHeight = el.offsetHeight;
      const scrolled = window.innerHeight - rect.top;
      const pct = Math.min(100, Math.round((scrolled / elHeight) * 100));

      checkpoints.forEach((cp) => {
        if (pct >= cp && !firedDepths.current.has(cp)) {
          firedDepths.current.add(cp);
          sendScrollBeacon(slug, cp, sessionId);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post, slug]);

  const handleNavigate = (section: string) => {
    navigate('/');
    setTimeout(() => {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    setIsMenuOpen(false);
  };

  if (loading) {
    return (
      <div style={commonStyles.container}>
        <Navigation
          theme={theme}
          activeSection="blog"
          isMenuOpen={false}
          isMobile={isMobile}
          onMenuToggle={() => {}}
          onNavigate={handleNavigate}
        />
        <div style={{ ...s.detailWrapper }}>
          <div style={s.detailContainer}>
            <div style={s.loading}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div style={commonStyles.container}>
        <Navigation
          theme={theme}
          activeSection="blog"
          isMenuOpen={false}
          isMobile={isMobile}
          onMenuToggle={() => {}}
          onNavigate={handleNavigate}
        />
        <div style={s.detailWrapper}>
          <div style={s.detailContainer}>
            <button style={s.backBtn} onClick={() => navigate('/blog')}>
              ← {t.blog.backToBlog}
            </button>
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>🔍</div>
              <p style={s.emptyText}>{t.blog.noPosts}</p>
            </div>
          </div>
        </div>
        <Footer theme={theme} onNavigateToLegal={(p) => navigate(`/${p}`)} />
      </div>
    );
  }

  return (
    <div style={commonStyles.container}>
      <Navigation
        theme={theme}
        activeSection="blog"
        isMenuOpen={isMenuOpen}
        isMobile={isMobile}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
        onNavigate={handleNavigate}
      />

      <div style={s.detailWrapper}>
        <div style={s.detailContainer}>
          {/* Back */}
          <button style={s.backBtn} onClick={() => navigate('/blog')}>
            ← {t.blog.backToBlog}
          </button>

          {/* Cover */}
          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt={post.title}
              style={s.heroCover}
            />
          )}

          {/* Title */}
          <h1 style={s.detailTitle}>{post.title}</h1>

          {/* Meta */}
          <div style={s.detailMeta}>
            {post.author && (
              <span>{t.blog.by} {post.author}</span>
            )}
            {post.published_at && (
              <span>{t.blog.publishedOn} {formatDate(post.published_at)}</span>
            )}
            <span>{post.read_time_minutes} {t.blog.minRead}</span>
            {post.view_count > 0 && (
              <span>{post.view_count.toLocaleString()} views</span>
            )}
            {post.tags?.length > 0 && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {post.tags.map((tag) => (
                  <span key={tag} style={s.detailTag}>{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Blocks */}
          <div ref={contentRef}>
            {post.blocks && post.blocks.length > 0 && (
              <BlockRenderer blocks={post.blocks} theme={theme} />
            )}
          </div>

          {/* Related posts */}
          {post.related_posts && post.related_posts.length > 0 && (
            <div style={s.relatedSection}>
              <h3 style={s.relatedTitle}>{t.blog.relatedPosts}</h3>
              <div style={s.relatedGrid}>
                {post.related_posts.map((related) => (
                  <div
                    key={related.id}
                    style={s.relatedCard}
                    onClick={() => {
                      navigate(`/blog/${related.slug}`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.transform = 'none';
                      (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                    }}
                  >
                    {related.cover_image_url && (
                      <img
                        src={related.cover_image_url}
                        alt={related.title}
                        style={s.relatedCardImg}
                        loading="lazy"
                      />
                    )}
                    <div style={s.relatedCardBody}>
                      <p style={s.relatedCardTitle}>{related.title}</p>
                      <p style={s.relatedCardMeta}>
                        {related.read_time_minutes} {t.blog.minRead}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer theme={theme} onNavigateToLegal={(p) => navigate(`/${p}`)} />
    </div>
  );
};

export default BlogDetailPage;
